import { useState, useCallback, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import SessionService from "../../../services/session_service";
import { useTenant } from "../../../hooks/useTenant";
import { addDays, toISODate, getStartOfWeek } from "../../../utils/date";

/**
 * useGradeData
 * Hook para buscar sessões reais do Firebase para a Grade Operacional.
 * Busca sessões por range de datas (semana ou dia) conforme a view.
 */
export const useGradeData = ({ referenceDate, view }) => {
    const { idTenant, loading: tenantLoading } = useTenant();
    const [sessions, setSessions] = useState([]);
    const [sessionsCache, setSessionsCache] = useState({});
    const [loading, setLoading] = useState(true);

    // Calcula o range de datas baseado na view
    const dateRange = useMemo(() => {
        if (view === "day") {
            const isoDate = toISODate(referenceDate);
            return { startDate: isoDate, endDate: isoDate };
        }

        // Week view: Dom a Sáb
        const weekStart = getStartOfWeek(referenceDate);
        const weekEnd = addDays(weekStart, 6);
        return {
            startDate: toISODate(weekStart),
            endDate: toISODate(weekEnd),
        };
    }, [referenceDate, view]);

    const fetchSessions = useCallback(async () => {
        if (!idTenant || tenantLoading) return;

        const cacheKey = `${dateRange.startDate}_${dateRange.endDate}_${view}`;
        if (sessionsCache[cacheKey]) {
            setSessions(sessionsCache[cacheKey]);
            return;
        }

        setLoading(true);
        try {
            const result = await SessionService.getSessions(
                idTenant,
                dateRange.startDate,
                dateRange.endDate
            );

            // Adiciona campo idSession para compatibilidade com a Grid
            const enriched = result.map(s => ({
                ...s,
                idSession: s.id,
                // A sessão tem sessionDate, a grid usa isso via occursOnDate
            }));

            setSessions(enriched);
            setSessionsCache(prev => ({ ...prev, [cacheKey]: enriched }));
        } catch (error) {
            console.error("[useGradeData] Erro ao buscar sessões:", error);
            toast.error("Erro ao carregar sessões da grade.");
            setSessions([]);
        } finally {
            setLoading(false);
        }
    }, [idTenant, tenantLoading, dateRange.startDate, dateRange.endDate, view, sessionsCache]);

    useEffect(() => {
        if (!tenantLoading && idTenant) {
            fetchSessions();
        }
    }, [tenantLoading, idTenant, fetchSessions]);

    return {
        sessions,
        loading,
        refresh: fetchSessions,
    };
};
